import React, { useEffect, useState, useCallback } from "react";
import { Button, FormLabel, Row, Col } from "react-bootstrap";
import { MdFullscreen, MdFullscreenExit } from "react-icons/md";
import { TableVirtuoso } from "react-virtuoso";
import noImage from "../images/clicktoAdd.png";
import { DropDwnDown, DropDwnUp } from "./Icons";
import { InputCheckbox, NumberFormatInputbox, InputTextbox } from "./UIComponents";
import PaginationComponent from "./PaginationComponent";
import {
  isEmpty,
  isEmptyArray,
  isEmptyString,
  toCommaSeperatedAmount,
  dateFormate,
} from "./Util";

// ─────────────────────────────────────────────
// Cell renderer – renders the correct content
// based on the column definition flags.
// ─────────────────────────────────────────────
function CellContent({ column, row, rowIndex, onTextChange, onBlur, onCheckboxChange, onCellClick }) {
  const value = row[column.accessor];

  // Input textbox
  if (column.isInputTexBox) {
    return (
      <input
        type="text"
        value={value}
        name={column.accessor}
        className="text-end col-sm-12 p-0 pr-1"
        onChange={(e) => onTextChange(e, rowIndex, column)}
        disabled={column.disable}
      />
    );
  }

  // Number-format inputbox
  if (column.isNumberFormat) {
    return (
      <NumberFormatInputbox
        name={column.accessor}
        onChange={(e) => onTextChange(e, rowIndex, column)}
        onBlur={(e) => onBlur(e, rowIndex, column)}
        className="text-end col-sm-12 p-0 pr-1"
        value={value}
        decimalScale={
          column.decimalScale != null &&
          column.decimalScale !== undefined &&
          column.decimalScale >= 0
            ? column.decimalScale
            : 2
        }
        disabled={column.disable}
      />
    );
  }

  // Checkbox
  if (column.isCheckBox) {
    return (
      <div className="table-flex">
        <InputCheckbox
          name={column.accessor}
          data-tag="allowRowEvents"
          onChange={(e) => onCheckboxChange(e, rowIndex, column)}
          checked={value}
          disabled={column.disable}
          disableLabel={true}
        />
      </div>
    );
  }

  // Button
  if (column.isButton) {
    return (
      <div className="table-flex">
        <Button
          className="table-btn"
          onClick={(e) => onCellClick(e, column, row)}
          disabled={column.disable}
          size="sm"
        >
          {column.buttonRender}
        </Button>
      </div>
    );
  }

  // Custom popup menu
  if (column.shortCutPopUpMenu) {
    return <div className="table-flex">{column.buttonRender(row)}</div>;
  }

  // Image
  if (column.isImage) {
    return (
      <div className="table-flex">
        <img
          src={value}
          onError={(e) => { e.target.onerror = null; e.target.src = noImage; }}
          alt={column.accessor}
          width={40}
          height={40}
          mode="fit"
        />
      </div>
    );
  }

  // Status badge
  if (column.isStatus) {
    return (
      <div className="status">
        {column.getStatusCaption ? column.getStatusCaption(value) : value}
      </div>
    );
  }

  // Graph
  if (column.isGraph) {
    const graphClass = column.getGraph ? column.getGraph(column, row, value) : "";
    return (
      <div className="grap-wrap">
        <div className={graphClass}></div>
      </div>
    );
  }

  // Hyperlink + numeric
  if (column.isHyperLink && column.isNumeric) {
    return (
      <div className="hyperLink" onClick={(e) => onCellClick(e, column, row)}>
        {toCommaSeperatedAmount(value)}
      </div>
    );
  }

  // Hyperlink (text)
  if (column.isHyperLink && !column.isNumeric) {
    return (
      <div className="hyperLink" onClick={(e) => onCellClick(e, column, row)}>
        {value}
      </div>
    );
  }

  // // Numeric (no hyperlink)
  // if (column.isNumeric && !column.isHyperLink && column.pre_currency) {
  //   return <div className="isNumberic">{toCommaSeperatedAmount(value)}</div>;
  // }

  if (column.isNumeric && !column.isHyperLink) {
  return (
    <div className="isNumberic">
      {column.pre_currency ? "$" : ""}
      {toCommaSeperatedAmount(value)}
    </div>
  );
}
  // Date
  if (column.isDateFormate) {
    return <div className="isDateFormate">{dateFormate(value)}</div>;
  }

  // Special (two-accessor)
  if (column.isSpecial) {
    return (
      <div className="hyperLink" onClick={(e) => onCellClick(e, column, row)}>
        {value} - {row[column.specialAccessor]}
      </div>
    );
  }

  // Plain text (default)
  return <div>{value}</div>;
}

// ─────────────────────────────────────────────
// Header cell renderer
// ─────────────────────────────────────────────
function HeaderCell({ column, sortConfig, onSort, onHeaderClick, subHeaderData }) {
  const isSortedAsc =
    sortConfig.key === column.accessor && sortConfig.direction === "asc";

  const SortIcon = isSortedAsc ? <DropDwnDown /> : <DropDwnUp />;

  return (
    <>
      {column.isHeaderButton ? (
        <div onClick={() => column.sortable && onSort(column.accessor)}>
          <Button
            onClick={(e) => onHeaderClick(e, column)}
            disabled={column.headerDisable}
            size="sm"
            id="table-header-btn"
          >
            {column.headerRender}
          </Button>
        </div>
      ) : (
        <div
          style={{ cursor: column.sortable ? "pointer" : "default" }}
          onClick={() => column.sortable && onSort(column.accessor)}
        >
          {column.header}
          {column.sortable && (
            <span className="short-toggle">{SortIcon}</span>
          )}
        </div>
      )}

      {/* Sub-header total */}
      {column.isSubHeader && !isEmpty(subHeaderData) && (
        <div className="bg-white text-danger text-end">
          {toCommaSeperatedAmount(subHeaderData[column.subHeaderAccessor])}
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────
// Main CustomTable component
// ─────────────────────────────────────────────
export default function CustomTable(props) {
  const [data, setData]               = useState(props.data   || []);
  const [columns, setColumns]         = useState(props.columns || []);
  const [subHeaderData, setSubHeaderData] = useState(props.subHeaderData);
  const [isFullscreen, setFullscreen] = useState(false);
  const [sortConfig, setSortConfig]   = useState({ key: null, direction: "asc" });

  // ── Sync props → state ──────────────────────
  useEffect(() => { setData(props.data || []); },          [props.data]);
  useEffect(() => { setColumns(props.columns || []); },    [props.columns]);
  useEffect(() => { setSubHeaderData(props.subHeaderData); }, [props.subHeaderData]);

  // ── Fullscreen escape key ───────────────────
  const handleEscapeKey = useCallback((e) => {
    if (e.key === "Escape") setFullscreen(false);
  }, []);

  const toggleFullscreen = () => {
    setFullscreen((prev) => {
      const next = !prev;
      next
        ? document.addEventListener("keydown", handleEscapeKey)
        : document.removeEventListener("keydown", handleEscapeKey);
      return next;
    });
  };

  // ── Sorting ─────────────────────────────────
  const requestSort = (key) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });

    if (!isEmptyArray(data)) {
      const sorted = [...data].sort((a, b) => {
        if (a[key] === null) return 1;
        if (b[key] === null) return -1;
        return (
          a[key].toString().localeCompare(b[key].toString(), "en", { numeric: true }) *
          (direction === "asc" ? 1 : -1)
        );
      });
      setData(sorted);
      if (typeof props.onSortComplete === "function") {
        props.onSortComplete(sorted, { key, direction });
      }
    }
  };

  // ── Cell event handlers ──────────────────────
  const handleCheckboxChange = (e, rowIndex, column) => {
    setData((prev) =>
      prev.map((item, idx) => {
        if (idx === rowIndex && column.checkBoxOnChange) column.checkBoxOnChange(e, item);
        return item;
      })
    );
  };

  const handleTextChange = (e, rowIndex, column) => {
    setData((prev) =>
      prev.map((item, idx) => {
        if (idx === rowIndex && column.inputOnChange) column.inputOnChange(e, item);
        return item;
      })
    );
  };

  const handleOnBlur = (e, rowIndex, column) => {
    setData((prev) =>
      prev.map((item, idx) => {
        if (idx === rowIndex && column.inputOnBlur) column.inputOnBlur(e, item);
        return item;
      })
    );
  };

  const handleCellClick = (e, column, row) => {
    if (column.clickHandler) column.clickHandler(e, row[column.accessor], row);
  };

  const handleHeaderClick = (e, column) => {
    if (column.headerClickHandler) column.headerClickHandler(e, column);
  };

  // ── CSS helpers ──────────────────────────────
  const getAlignClass = (alignment) => {
    if (alignment === "left")   return "text-start";
    if (alignment === "right")  return "text-end";
    if (alignment === "center") return "text-center";
    return "";
  };

  const getCellClass = (column) => {
    const parts = [
      getAlignClass(column.alignment),
      column.mininWidth ? `min-wt-${column.mininWidth}` : "",
      column.maxWidth   ? `max-wt-${column.maxWidth}`   : "",
      column.customStyle || "",
    ];
    return parts.filter(Boolean).join(" ");
  };

  const getHeaderClass = (column) => {
    const parts = [
      getAlignClass(column.alignment),
      column.mininWidth   ? `min-wt-${column.mininWidth}` : "",
      column.maxWidth     ? `max-wt-${column.maxWidth}`   : "",
      column.headerStyle  || "",
    ];
    return parts.filter(Boolean).join(" ");
  };

  // ── Prop callbacks (pass-through) ────────────
  const call = (fn, e) => { if (typeof fn === "function") fn(e); };

  // ── Shared header renderer ───────────────────
  const renderHeader = () => (
    <>
      {/* Row 1 – main headers */}
      <tr>
        {columns.map((col, idx) =>
          col.isColSpan ? null : (
            <th
              key={idx}
              rowSpan={!col.isTopHeader ? 2 : undefined}
              colSpan={col.isTopHeader ? col.colSpan : undefined}
              className={getHeaderClass(col)}
            >
              {col.isTopHeader ? (
                <div>{col.header}</div>
              ) : (
                <HeaderCell
                  column={col}
                  sortConfig={sortConfig}
                  onSort={requestSort}
                  onHeaderClick={handleHeaderClick}
                  subHeaderData={subHeaderData}
                />
              )}
            </th>
          )
        )}
      </tr>

      {/* Row 2 – colspan sub-headers */}
      {props.isEnableSpan && (
        <tr>
          {columns.map((col, idx) =>
            col.isColSpan ? (
              <th key={idx} className={getHeaderClass(col)}>
                <HeaderCell
                  column={col}
                  sortConfig={sortConfig}
                  onSort={requestSort}
                  onHeaderClick={handleHeaderClick}
                  subHeaderData={subHeaderData}
                />
              </th>
            ) : null
          )}
        </tr>
      )}
    </>
  );

  // ── Shared row renderer ──────────────────────
  const renderRow = (row, rowIndex) =>
    columns.map((col, colIdx) =>
      col.isTopHeader ? null : (
        <td key={colIdx} className={getCellClass(col)}>
          <CellContent
            column={col}
            row={row}
            rowIndex={rowIndex}
            onTextChange={handleTextChange}
            onBlur={handleOnBlur}
            onCheckboxChange={handleCheckboxChange}
            onCellClick={handleCellClick}
          />
        </td>
      )
    );

  // ── Table toolbar ────────────────────────────
  const renderToolbar = (isVirtualized = false) => (
    <div className="title-wrap">
      <div className="title">
        <Row>
          {props.label && (
            <FormLabel className="table_title">{props.label}</FormLabel>
          )}
          {props.isShowLabelButton && (
            <Button
              size="sm"
              id="fabric_Add_Button"
              onClick={(e) => call(props.labelButtonClickHandler, e)}
              disabled={props.isLabelButtonDisable}
            >
              {props.labelButtonRender || "Add"}
            </Button>
          )}
          {props.isShowLabelButton && props.labelButtonRenderPDF && (
            <Button
              size="sm"
              id="fabric_Add_Button"
              onClick={(e) => call(props.labelPDFButtonClickHandler, e)}
              disabled={props.isLabelButtonDisable}
            >
              {props.labelButtonRenderPDF}
            </Button>
          )}
          {props.isShowExcelResetButton && (
            <Button
              size="sm"
              onClick={(e) => call(props.excelResetButtonClickHandler, e)}
              disabled={props.isExcelResetButtonDisable}
            >
              {props.renderExcelResetButton}
            </Button>
          )}
        </Row>
      </div>

      {/* Select / Unselect (non-virtualized only) */}
      {!isVirtualized && props.isShowSelectButton && (
        <div className="Select-bts">
          <Button
            className="table-label-btn_v1"
            onClick={(e) => call(props.selectPtLines, e)}
            disabled={props.isSelectCriteria}
          >
            Select
          </Button>
          <Button
            className="table-label-btn_v1"
            onClick={(e) => call(props.unselectPtLines, e)}
            disabled={props.isUnSelectCriteria}
          >
            Un Select
          </Button>
        </div>
      )}

      <div className="">
        {props.isShowChartButton && (
          <Button
            size={isVirtualized ? "lg" : "sm"}
            id="fabric_Add_Button"
            onClick={(e) => call(props.chartButtonClickHandler, e)}
            disabled={props.isLabelButtonDisable}
          >
            {props.chartButtonRender}
          </Button>
        )}
        {isFullscreen ? (
          <MdFullscreenExit onClick={toggleFullscreen} className="full-screen-toggle" />
        ) : (
          <MdFullscreen onClick={toggleFullscreen} className="full-screen-toggle" />
        )}
      </div>
    </div>
  );

  // ── Sub-header input fields ──────────────────
  const renderSubHeaderInputs = () =>
    props.isShowInputTextbox && subHeaderData ? (
      <Row>
        <div>
          <Row>
            {[1, 2, 3, 4].map((n) => (
              <React.Fragment key={n}>
                <Col sm="1">
                  <FormLabel>{subHeaderData[`lab_${n}`]}</FormLabel>
                </Col>
                <Col sm="2">
                  <InputTextbox
                    name={`value_${n}`}
                    type="text"
                    value={subHeaderData[`value_${n}`]}
                    disabled={true}
                  />
                </Col>
              </React.Fragment>
            ))}
          </Row>
          {subHeaderData.lab_5 && (
            <Row>
              <Col sm="2">
                <FormLabel>{subHeaderData.lab_5}</FormLabel>
              </Col>
              <Col sm="2">
                <InputTextbox
                  name="value_5"
                  type="text"
                  value={subHeaderData.value_5}
                  disabled={true}
                />
              </Col>
            </Row>
          )}
        </div>
      </Row>
    ) : null;

  const wrapClass = `header_div table-wrap card${isFullscreen ? " full-screen" : ""}`;

  // ════════════════════════════════════════════
  // VIRTUALIZED mode  (enableVritualizer)
  // ════════════════════════════════════════════
  if (props.enableVritualizer) {
    return (
      <div className={wrapClass}>
        {renderToolbar(true)}
        <TableVirtuoso
          style={{ height: isFullscreen ? "100vh" : "80vh" }}
          data={data}
          components={{
            Table: ({ style, ...rest }) => (
              <table
                id="custom-table"
                className={props.isResizable ? "resizable" : ""}
                {...rest}
                style={{ ...style }}
              />
            ),
          }}
          fixedHeaderContent={renderHeader}
          itemContent={(rowIndex, row) => (
            <>
              {renderRow(row, rowIndex)}
            </>
          )}
        />
        {data.length === 0 && (
          <div className="no_Data_small">NO DATA</div>
        )}
      </div>
    );
  }

  // ════════════════════════════════════════════
  // VIRTUALIZE prop  (enableVritualize – dashboard panels)
  // ════════════════════════════════════════════
  if (props.enableVritualize) {
    return (
      <>
        <table id="custom-table" className={props.isResizable ? "resizable" : ""}>
          <thead>{renderHeader()}</thead>
          <tbody>
            {data.map((row, rowIndex) => (
              <tr key={rowIndex}>{renderRow(row, rowIndex)}</tr>
            ))}
          </tbody>
        </table>
        {data.length === 0 && (
          <div className="no_Data_small">NO DATA</div>
        )}
      </>
    );
  }

  // ════════════════════════════════════════════
  // STANDARD mode
  // ════════════════════════════════════════════
  return (
    <div className={`${wrapClass} ${props.cstmStyle || ""}`}>
      {renderToolbar(false)}

      {renderSubHeaderInputs()}

      {props.isShowPagination && (
        <div className="custom_Pagination">
          <PaginationComponent
            loadPrevious={(e) => call(props.loadPrevious, e)}
            loadNext={(e) => call(props.loadNext, e)}
            currentPage={props.currentPage}
            totalPages={props.totalPages}
            handleOnchange={(e) => call(props.handleOnchange, e)}
            handlePaginationOnBlur={(e) => call(props.handlePaginationOnBlur, e)}
            onPageClick={(e) => call(props.onPageClick, e)}
          />
        </div>
      )}

      <table id="custom-table" className={props.isResizable ? "resizable" : ""}>
        <thead>{renderHeader()}</thead>
        {data.length > 0 && (
          <tbody>
            {data.map((row, rowIndex) => (
              <tr key={rowIndex}>{renderRow(row, rowIndex)}</tr>
            ))}
          </tbody>
        )}
      </table>

      {data.length === 0 && (
        <div className="no_Data_small">NO DATA</div>
      )}
    </div>
  );
}
